import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import type { BundleImportItem } from '@/lib/api/generated/model';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useAssignablePeople, useIsOrgAdmin } from '@/features/identity';
import { useLlmProfiles } from '@/features/llm-profiles';

import { isWorkflowRejection } from '../errors';
import { fileIdOf, useBundleCheck } from '../hooks/useBundleCheck';
import { useImportBundle } from '../hooks/useBundleImport';
import { useSettledValue } from '../hooks/useSettledValue';
import {
  initialAnswers,
  outstanding,
  toRequest,
  toRows,
  type Answers,
} from '../upload/answers';
import {
  itemId,
  sameAnswers,
  toBundleAnswers,
  type BundleChoices,
  type ItemChoice,
} from '../upload/bundleAnswers';
import {
  answerAllWithOurs,
  counted,
  groupLabel,
  markerOf,
  reviewOf,
  type MarkerTone,
} from '../upload/bundleReview';
import { FlowDialogPending } from './FlowDialog';
import { ResolveReferences } from './ResolveReferences';

const KIND_LABEL: Record<BundleImportItem['kind'], string> = {
  workflow: 'Workflow',
  agent: 'Agent',
  skill: 'Skill',
};

/** How long answers must stay unchanged before they are checked (spec 208). */
const SETTLE_MS = 400;

/**
 * A bundle's import: see what it holds, answer what is already here, bring it
 * all in (spec 204; reworked by spec 208; service spec 163).
 *
 * **It checks by itself.** A new file (or, in Copy, a new target) is checked at
 * once, and answers are checked again once they have settled, so the person
 * never presses Check. Only the check made with the current answers counts.
 *
 * **Questions first.** One summary line, then the items that need the person,
 * then everything else folded: in the review's scene 2 of 9 items need an
 * answer, and they used to sit in the middle of the list. Answering changes a
 * row's marker, never its height.
 *
 * **Import is the one button**, always. With questions open it imports nothing:
 * it takes the person to the first open question and says how many remain,
 * instead of being disabled without a word. Nothing is written before Import,
 * and nothing here is replaced unless the person chose Replace.
 *
 * It lays nothing out itself (spec 207): the dialog it sits in places the body,
 * the button and the alert. With no file it offers no parts at all, so a dialog
 * can sit inside it on every face; a new file or workspace starts afresh.
 */
export function BundleImport({
  workspaceId,
  file,
  onDone,
  onBusyChange,
  onDescribe,
  children,
}: {
  workspaceId: string;
  /** The bundle chosen; `null` while the dialog's other face is showing. */
  file: File | null;
  /** The import went through; the dialog closes. */
  onDone: () => void;
  /** Whether an import is running, during which the dialog cannot close. */
  onBusyChange?: (busy: boolean) => void;
  /** The bundle's root workflow and item count once checked, for a file chip. */
  onDescribe?: (described: { root: string; items: number } | null) => void;
  children: (parts: {
    /** The summary, the questions, the folded groups and any refusal. */
    body: ReactNode;
    /** Always Import. */
    action: ReactNode;
    /** A failed check or import, or how many questions are left. */
    alert: ReactNode | null;
  }) => ReactNode;
}) {
  const importing = useImportBundle(workspaceId);
  const profiles = useLlmProfiles();
  const people = useAssignablePeople(workspaceId, { enabled: file !== null });
  const isOrgAdmin = useIsOrgAdmin();

  const [choices, setChoices] = useState<BundleChoices>({});
  const [references, setReferences] = useState<Answers | null>(null);
  const [prompted, setPrompted] = useState(false);
  // A different file or workspace is a different question: every answer goes.
  const subject = file ? `${workspaceId}:${String(fileIdOf(file))}` : null;
  const [answeredFor, setAnsweredFor] = useState(subject);
  if (answeredFor !== subject) {
    setAnsweredFor(subject);
    setChoices({});
    setReferences(null);
    setPrompted(false);
  }

  const current = toBundleAnswers(
    choices,
    references ? toRequest(references) : undefined,
  );
  const settled = useSettledValue(current, SETTLE_MS, sameAnswers, subject);
  const check = useBundleCheck(workspaceId, file, settled);
  const preview = check.preview;

  const rows = preview ? toRows(preview.unresolved ?? []) : [];
  // Profiles and people are asked about from the first check that names them.
  if (rows.length > 0 && references === null) {
    setReferences(initialAnswers(rows));
  }
  const referencesLeft = references ? outstanding(rows, references).length : 0;
  const review = preview ? reviewOf(preview, choices, referencesLeft) : null;

  const failure = importing.error ?? check.failure;
  const violations = isWorkflowRejection(failure)
    ? failure.violations
    : (preview?.violations ?? []);
  const blockers = preview?.blockers ?? [];
  const refused = blockers.length > 0 || violations.length > 0;
  const upToDate = sameAnswers(current, settled) && check.current;

  useEffect(() => {
    onBusyChange?.(importing.isPending);
  }, [importing.isPending, onBusyChange]);

  const rootName = preview
    ? (preview.items.find((item) => item.root)?.name ?? preview.root)
    : null;
  const itemCount = preview?.items.length ?? 0;
  useEffect(() => {
    onDescribe?.(rootName ? { root: rootName, items: itemCount } : null);
  }, [rootName, itemCount, onDescribe]);

  // Where Import sends the person while questions are open.
  const firstControls = useRef(new Map<string, HTMLElement | null>());

  if (!file) return children({ body: null, action: null, alert: null });

  const bringIn = () => {
    if (!review) return;
    if (review.open > 0) {
      setPrompted(true);
      firstControls.current.get(review.firstOpen ?? '')?.focus();
      return;
    }
    importing.mutate(
      { file, answers: current },
      {
        onSuccess: (result) => {
          if (result.blocked) {
            // Profiles or people still unanswered: ask the service again.
            check.retry();
            return;
          }
          const added = (result.outcomes ?? []).filter(
            (o) => o.outcome === 'added',
          ).length;
          toast.success(
            added === 0
              ? 'Everything in this bundle is already here.'
              : `Imported ${counted(added, 'item', 'items')}${result.workflow ? `, starting with ${result.workflow.name}` : ''}.`,
          );
          onDone();
        },
      },
    );
  };

  const questionCount = (review?.questions.length ?? 0) + rows.length;
  const body = !preview ? (
    check.checking ? (
      <FlowDialogPending label={`Checking ${file.name}…`} />
    ) : null
  ) : (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <p role="status" className="text-sm font-medium">
          {review?.summary}
        </p>
        {preview.source_workspace && (
          <p className="text-muted-foreground text-xs">
            Exported from {preview.source_workspace}
            {preview.exported_at
              ? ` on ${preview.exported_at.slice(0, 10)}`
              : ''}
            .
          </p>
        )}
      </div>

      {blockers.length > 0 && (
        <ul
          role="alert"
          className="text-destructive flex flex-col gap-1 text-sm"
        >
          {blockers.map((blocker) => (
            <li key={blocker}>{blocker}</li>
          ))}
        </ul>
      )}
      {violations.length > 0 && (
        <ul
          role="alert"
          aria-label="Import rejected"
          className="text-destructive flex flex-col gap-1 text-sm"
        >
          {violations.map((violation, index) => (
            <li key={index}>{violation.message}</li>
          ))}
        </ul>
      )}

      {questionCount > 0 && review && (
        <section aria-label="Needs your answer" className="flex flex-col gap-2">
          <div className="flex min-h-9 items-center gap-2">
            <h3 className="text-sm font-medium">
              Needs your answer ({questionCount})
            </h3>
            {review.questions.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={importing.isPending}
                onClick={() =>
                  setChoices(answerAllWithOurs(review.questions, choices))
                }
              >
                Use the ones here for all
              </Button>
            )}
          </div>
          {review.questions.length > 0 && (
            <ul className="flex flex-col gap-2">
              {review.questions.map((item) => (
                <QuestionRow
                  key={itemId(item)}
                  item={item}
                  choice={choices[itemId(item)]}
                  onChoose={(choice) =>
                    setChoices({ ...choices, [itemId(item)]: choice })
                  }
                  disabled={importing.isPending}
                  firstControl={(node) => {
                    firstControls.current.set(itemId(item), node);
                  }}
                />
              ))}
            </ul>
          )}
          {references && rows.length > 0 && (
            <div
              ref={(node) => {
                firstControls.current.set(
                  'references',
                  node?.querySelector<HTMLElement>(
                    'input, button, [role=combobox]',
                  ) ?? null,
                );
              }}
            >
              <ResolveReferences
                rows={rows}
                answers={references}
                onAnswersChange={setReferences}
                profiles={profiles.entries ?? []}
                profilesUnavailable={profiles.loadFailed}
                canCreateProfiles={isOrgAdmin}
                people={people.people}
                directoryUnavailable={people.failed}
              />
            </div>
          )}
        </section>
      )}

      {review && review.fresh.length > 0 && (
        <FoldedGroup
          label={groupLabel('new', review.fresh)}
          items={review.fresh}
        />
      )}
      {review && review.here.length > 0 && (
        <FoldedGroup
          label={groupLabel('here', review.here)}
          items={review.here}
        />
      )}
    </div>
  );

  const open = review?.open ?? 0;
  // Every question answered, and their check on its way: Import waits for it,
  // and says so rather than sitting greyed out with no reason.
  const waiting =
    review !== null && open === 0 && !refused && !upToDate && !check.failure;
  const action = (
    <Button
      onClick={bringIn}
      aria-busy={waiting || importing.isPending || undefined}
      disabled={
        !review ||
        refused ||
        importing.isPending ||
        (open === 0 && (!upToDate || preview?.importable !== true))
      }
    >
      {(waiting || importing.isPending) && (
        <Loader2Icon aria-hidden="true" className="motion-safe:animate-spin" />
      )}
      {importing.isPending ? 'Importing…' : 'Import'}
    </Button>
  );

  const alert =
    check.failure && !isWorkflowRejection(check.failure) ? (
      <span className="flex flex-wrap items-center gap-2">
        {check.failure.message}
        <Button variant="outline" size="sm" onClick={check.retry}>
          Try again
        </Button>
      </span>
    ) : importing.error && !isWorkflowRejection(importing.error) ? (
      importing.error.message
    ) : prompted && open > 0 ? (
      `Answer ${counted(open, 'question', 'questions')} to import.`
    ) : null;

  return children({ body, action, alert });
}

const TONE: Record<MarkerTone, string> = {
  neutral: 'border-border text-foreground',
  success: 'border-transparent bg-state-success/10 text-state-success',
  warning: 'border-transparent bg-state-warning/10 text-state-warning',
};

function Marker({ word, tone }: { word: string; tone: MarkerTone }) {
  return (
    <Badge variant="outline" className={cn('ml-auto', TONE[tone])}>
      {word}
    </Badge>
  );
}

/**
 * One item the person decides about. Its height is the same answered or not:
 * the new-key field has its place reserved, and only the marker changes.
 */
function QuestionRow({
  item,
  choice,
  onChoose,
  disabled,
  firstControl,
}: {
  item: BundleImportItem;
  choice: ItemChoice | undefined;
  onChoose: (choice: ItemChoice) => void;
  disabled: boolean;
  firstControl: (node: HTMLInputElement | null) => void;
}) {
  const keyId = useId();
  const isWorkflow = item.kind === 'workflow';
  const renaming = choice?.action === 'rename';
  const suggested = item.new_key ?? item.suggested_key ?? `${item.key}-2`;
  const usedBy = item.used_by ?? [];

  return (
    <li className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground w-16 shrink-0 text-xs">
          {KIND_LABEL[item.kind]}
        </span>
        <span className="truncate font-medium">{item.name}</span>
        <Marker {...markerOf(item, choice)} />
      </div>
      <p className="text-muted-foreground text-xs">
        This workspace already has a different {item.kind} called {item.key}.
        {usedBy.length > 0 && ` Used here by ${usedBy.join(', ')}.`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <AnswerChoice
          label={`What to do with ${item.name}`}
          value={choice?.action}
          disabled={disabled}
          firstControl={firstControl}
          options={[
            { value: 'use_existing', label: 'Use ours' },
            isWorkflow
              ? { value: 'rename', label: 'Import as new key' }
              : { value: 'replace', label: 'Replace' },
          ]}
          onChange={(action) =>
            onChoose(
              action === 'rename'
                ? { action: 'rename', newKey: suggested }
                : { action },
            )
          }
        />
        {isWorkflow && (
          // Always laid out, shown only for a rename: choosing it moves nothing.
          <div
            className={cn('flex items-center gap-2', !renaming && 'invisible')}
          >
            <label htmlFor={keyId} className="text-muted-foreground text-xs">
              New key
            </label>
            <Input
              id={keyId}
              className="h-9 w-40"
              disabled={disabled || !renaming}
              value={renaming ? choice.newKey : suggested}
              onChange={(event) =>
                onChoose({ action: 'rename', newKey: event.target.value })
              }
            />
          </div>
        )}
      </div>
    </li>
  );
}

type Action = ItemChoice['action'];

/**
 * Two answers side by side, drawn as one segmented control over native radios,
 * so arrow keys move between them and a screen reader hears a radio group.
 */
function AnswerChoice({
  label,
  options,
  value,
  onChange,
  disabled,
  firstControl,
}: {
  label: string;
  options: { value: Action; label: string }[];
  value: Action | undefined;
  onChange: (value: Action) => void;
  disabled: boolean;
  firstControl: (node: HTMLInputElement | null) => void;
}) {
  const name = useId();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="border-border inline-flex gap-0.5 rounded-md border p-0.5"
    >
      {options.map((option, index) => (
        <label
          key={option.value}
          className="hover:bg-muted/60 has-checked:bg-primary has-checked:text-primary-foreground has-checked:hover:bg-primary has-focus-visible:ring-ring/50 touch-target flex cursor-pointer items-center rounded-sm px-3 py-2 text-sm has-disabled:cursor-default has-disabled:opacity-60 has-focus-visible:ring-3"
        >
          <input
            ref={index === 0 ? firstControl : undefined}
            type="radio"
            name={name}
            className="sr-only"
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/** "7 new: 2 workflows, 5 agents", opening to the list. */
function FoldedGroup({
  label,
  items,
}: {
  label: string;
  items: BundleImportItem[];
}) {
  return (
    <Collapsible className="border-border rounded-md border">
      <CollapsibleTrigger className="hover:bg-muted/50 group touch-target flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm">
        <ChevronRightIcon
          aria-hidden="true"
          className="text-muted-foreground size-4 shrink-0 transition-transform duration-[var(--motion-duration-base)] group-data-panel-open:rotate-90"
        />
        {label}
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <ul className="border-border flex flex-col border-t px-3 py-1">
          {items.map((item) => (
            <li
              key={itemId(item)}
              className="flex items-center gap-2 py-1.5 text-sm"
            >
              <span className="text-muted-foreground w-16 shrink-0 text-xs">
                {KIND_LABEL[item.kind]}
              </span>
              <span className="truncate">{item.name}</span>
            </li>
          ))}
        </ul>
      </CollapsiblePanel>
    </Collapsible>
  );
}
